import { Link } from 'react-router-dom';
import LearnPage from './LearnPage';

const FAQS = [
  {
    q: 'How much capital do I need to generate $5,000/month in dividends?',
    a: 'Roughly $1.5M at a 4% blended yield, $1.0M at 6%, or $750K at 8%. The lower the yield, the larger the capital required — but higher yields usually mean lower growth and more sustainability risk. Most realistic plans target a 4-5% blended yield with mid-single-digit dividend growth on top, requiring around $1.2-$1.5M.',
  },
  {
    q: 'How long does it take to build that?',
    a: 'Depending on contribution rate and starting capital: $500/month + 7% returns = ~35 years to $1M. $1,500/month + 7% returns = ~22 years. $3,000/month + 7% returns = ~15 years. DRIP shaves another 2-3 years off each scenario by compounding faster.',
  },
  {
    q: 'Should I focus on yield or growth in the build phase?',
    a: 'Growth. A 2% yielder with 10% annual dividend growth will outpace a 6% yielder with 1% growth within 12 years AND have more total capital appreciation. The high-current-yield strategy only makes sense when you actually need the income now (retirement / income-replacement phase).',
  },
  {
    q: 'Should the $5K target be in one account or spread across accounts?',
    a: 'Spread. Hold ordinary-income high-yielders (REITs, BDCs, covered-call ETFs) in your Roth IRA — they generate the biggest after-tax wins there. Hold qualified-dividend payers (SCHD, KO, JNJ) in taxable for the 15% preferential rate. The same $5K monthly can leave you with very different after-tax dollars depending on where it sits.',
  },
  {
    q: 'What if the market crashes during my build?',
    a: 'It will. Build phases that span 20-35 years cross multiple crashes by definition. The dividend growers in this kind of portfolio kept paying through 2008, 2020, and every other downturn. Continuing to DRIP during crashes — buying more shares at lower prices — is the single biggest accelerator of dividend income for long-horizon builders.',
  },
];

export default function LearnBuild5kMonthly() {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: a },
    })),
  };

  return (
    <LearnPage
      title="How to Build a $5,000 Monthly Dividend Income Portfolio — Worked Example | DiviDrip"
      description="A concrete blueprint for building a portfolio that pays $5,000/month in dividends. Capital required, tier sizing, account placement, expected timeline, and the role of DRIP."
      canonical="https://twylightcrow.com/learn/build-5k-monthly-income"
      guidePath="/learn/build-5k-monthly-income"
      sectionLabel="Practical Workflows"
      sectionAnchor="workflows"
      kicker="Learn · Worked example"
      h1="Build a $5,000 Monthly Dividend Income Portfolio"
      datePublished="2026-05-23"
      jsonLd={jsonLd}
      related={[
        { to: '/learn/monthly-dividend-calendar', label: 'The monthly dividend calendar trick' },
        { to: '/learn/first-10-tickers', label: 'The first 10 tickers — starting with $10K' },
        { to: '/learn/four-phases', label: 'The 4 phases of a dividend investor\'s life' },
        { to: '/learn/why-drip-roth', label: 'Why I DRIP in my Roth but not in my taxable' },
        { to: '/learn/yield-on-cost', label: 'Yield on cost — your real long-term yield' },
      ]}
    >
      <p>
        $5,000/month — $60,000/year — is roughly what a married couple needs to cover
        basic expenses in most of the US in retirement (housing usually paid off,
        healthcare from Medicare + a supplement, modest lifestyle). It's also a clean
        round number that makes the math easy to follow.
      </p>
      <p>
        Here's a concrete blueprint for getting there.
      </p>

      <h2>The math: how much capital you need</h2>
      <table className="learn-table">
        <thead>
          <tr>
            <th className="text-left font-semibold">Blended yield</th>
            <th className="text-left font-semibold">Capital needed</th>
            <th className="text-left font-semibold">Typical mix</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>3%</td>
            <td className="font-mono">$2.0M</td>
            <td>All dividend-growth focused (SCHD, JNJ, KO)</td>
          </tr>
          <tr>
            <td>4%</td>
            <td className="font-mono">$1.5M</td>
            <td>Balanced growth + income</td>
          </tr>
          <tr>
            <td>5%</td>
            <td className="font-mono">$1.2M</td>
            <td>Growth + REITs + some BDCs</td>
          </tr>
          <tr>
            <td>6%</td>
            <td className="font-mono">$1.0M</td>
            <td>Heavy REITs/BDCs/CEFs</td>
          </tr>
          <tr>
            <td>8%</td>
            <td className="font-mono">$750K</td>
            <td>High-yield ETFs + CCs + some risk</td>
          </tr>
        </tbody>
      </table>
      <p>
        Most realistic plans target the 4-5% range — high enough that you don't need
        $2M, low enough that the dividends keep growing and the underlying capital
        isn't at extreme yield-trap risk.
      </p>

      <h2>The example portfolio ($1.5M, 4% blended)</h2>
      <p>
        Allocate $1.5M across roughly these buckets:
      </p>
      <ul>
        <li><strong>$600K (40%) — Dividend-growth core ETFs.</strong> SCHD ($400K), DGRO ($200K). Yield ~3.5%. Income: ~$21,000/year.</li>
        <li><strong>$300K (20%) — Dividend Kings/Aristocrats.</strong> JNJ, KO, PG, PEP, LOW. Yield ~3%. Income: ~$9,000/year.</li>
        <li><strong>$300K (20%) — REITs + Utilities.</strong> O, STAG, MAIN, NEE, SO. Yield ~5%. Income: ~$15,000/year.</li>
        <li><strong>$200K (13%) — Covered call income ETFs.</strong> JEPI, JEPQ, DIVO. Yield ~7%. Income: ~$14,000/year.</li>
        <li><strong>$100K (7%) — Cash + reserve.</strong> Short-term Treasuries / money market. ~$4,500/year.</li>
      </ul>
      <p>
        <strong>Total annual income: ~$63,500. Monthly: ~$5,290.</strong> Slightly
        above the $5K target with cushion for occasional dividend cuts.
      </p>

      <h2>Account placement matters as much as ticker mix</h2>
      <p>
        Where each bucket sits dramatically affects your after-tax monthly income.
        Suggested placement:
      </p>
      <ul>
        <li><strong>Roth IRA / Roth 401(k):</strong> REITs ($150K), BDCs ($100K), covered-call ETFs ($150K), high-yield CEFs ($100K). These pay ordinary income and the Roth tax-free withdrawal is most valuable here.</li>
        <li><strong>Traditional IRA / 401(k):</strong> Mix of growth ETFs and dividend payers — placement is tax-neutral here, optimise for whatever fits the Roth-conversion plan.</li>
        <li><strong>Taxable brokerage:</strong> SCHD, DGRO, Dividend Kings (JNJ, KO, PG). These pay qualified dividends at the preferred 15-20% rate.</li>
      </ul>

      <h2>The ramp — getting from zero to $1.5M</h2>
      <p>
        Three sample paths to $1.5M at 7% real returns:
      </p>
      <ul>
        <li><strong>$500/month + DRIP:</strong> ~35 years</li>
        <li><strong>$1,000/month + DRIP:</strong> ~28 years</li>
        <li><strong>$2,000/month + DRIP:</strong> ~22 years</li>
        <li><strong>$3,000/month + DRIP:</strong> ~17 years</li>
      </ul>
      <p>
        Notice how the ramp time barely improves past $2K/month — the math is largely
        time-driven, not contribution-driven, beyond a certain rate. The single best
        accelerator after $2K/month isn't saving more; it's starting earlier.
      </p>

      <h2>The first 5 years (Accumulator mode)</h2>
      <ul>
        <li>Max your Roth IRA ($7,500 in 2026; $8,600 total if 50+ with catch-up)</li>
        <li>Get at least the company 401(k) match</li>
        <li>Hold mostly broad ETFs (SCHD + VTI is fine as a two-fund portfolio)</li>
        <li>DRIP everything</li>
        <li>Ignore the income number. Focus on the share count.</li>
      </ul>

      <h2>Years 5-15 (Growth-Income mode)</h2>
      <ul>
        <li>Continue maxing Roth + 401(k)</li>
        <li>Add a few individual Kings/Aristocrats positions (JNJ, KO, PG)</li>
        <li>Start tracking your monthly dividend income in DiviDrip — it becomes motivational once it crosses $200/month</li>
        <li>Still DRIP everything in tax-advantaged accounts</li>
      </ul>

      <h2>Years 15-25 (Pre-retirement)</h2>
      <ul>
        <li>Start adding income-tilted positions (REITs, BDCs, covered-call ETFs) in your Roth</li>
        <li>Consider turning off DRIP on individual taxable holdings</li>
        <li>Plan Roth conversions if you're in a low-tax window</li>
      </ul>

      <h2>FAQ</h2>
      <dl className="learn-faq">
        {FAQS.map(({ q, a }) => (
          <div key={q} className="my-4">
            <dt className="font-semibold text-foreground">{q}</dt>
            <dd className="mt-1 text-foreground/85">{a}</dd>
          </div>
        ))}
      </dl>

      <h2>Track your progress in DiviDrip</h2>
      <p>
        Open <Link to="/dashboard">DiviDrip</Link> and look at the{' '}
        <strong>Portfolio Summary</strong> section at the top of the My Portfolio
        page. The <strong>Annual Dividends</strong> tile shows your forward annual
        income with a "$X/mo" subtitle — that monthly number is the one to track
        against your $5,000 target. The <strong>Forward · Last 365d actual</strong>{' '}
        comparison pill below it shows the gap between today's projected forward
        income and what actually landed in your account over the trailing twelve
        months — a useful sanity check for any pending raises. Check the figures
        once a quarter; if the monthly number isn't moving, your contribution
        rate or yield mix needs adjusting.
      </p>
      <p className="text-xs text-muted-foreground italic mt-6">
        Not investment advice. The portfolio mix shown is illustrative, not a recommendation.
      </p>
    </LearnPage>
  );
}
